/**
 * Hook for fetching and managing connections (user-scoped or group-scoped).
 *
 * @module hooks/useConnections
 */

import { useQuery } from '@tanstack/react-query';
import { connectionService } from '@/services';
import type { ConnectionResponse } from '@/types';

/**
 * Fetches connections for the current scope.
 * When sandboxGroupId is provided, fetches group-scoped connections.
 * Otherwise, fetches user-scoped connections.
 */
export function useConnections(sandboxGroupId?: string) {
  const query = useQuery<ConnectionResponse[]>({
    queryKey: sandboxGroupId ? ['connections', 'group', sandboxGroupId] : ['connections'],
    queryFn: () =>
      sandboxGroupId ? connectionService.listForGroup(sandboxGroupId) : connectionService.list(),
    staleTime: 60_000, // Cache for 1 minute
    refetchOnWindowFocus: false,
  });

  // Build a lookup map for connection names
  const connectionNames = new Map<string, string>();
  if (query.data) {
    for (const conn of query.data) {
      connectionNames.set(conn.id, conn.name);
    }
  }

  return {
    ...query,
    connections: query.data ?? [],
    connectionNames,
    /** Get connection name by ID, returns undefined if not found */
    getConnectionName: (id: string) => connectionNames.get(id),
  };
}
